import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, useLocation } from 'react-router-dom'
import { Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { Input } from '@/components/common/Input'
import { Button } from '@/components/common/Button'
import { useAuthStore } from '@/stores/authStore'
import { authApi, getApiErrorMessage } from '@/services/api'
import { loginSchema, type LoginFormData } from '@/utils/validators'
import type { User, AuthTokens } from '@/types'

interface LoginApiResponse {
  data: {
    user: User
    tokens: AuthTokens
    accessToken?: string
    refreshToken?: string
  }
  // Some backends return tokens at the top level of data
  user?: User
  tokens?: AuthTokens
}

export function LoginForm() {
  const navigate = useNavigate()
  const location = useLocation()
  const login = useAuthStore((state) => state.login)
  const [showPassword, setShowPassword] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  // Navigate to the page the user was trying to access, or dashboard
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/'

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = async (data: LoginFormData) => {
    setApiError(null)

    try {
      const response = await authApi.login(data.email, data.password)
      // After interceptor unwrap: response.data = { accessToken, refreshToken, user }
      const responseData = response.data as any

      const rawUser = responseData.user as any
      const tokens: AuthTokens = responseData.tokens ?? {
        accessToken: responseData.accessToken as string,
        refreshToken: responseData.refreshToken as string,
      }

      if (!rawUser || !tokens?.accessToken) {
        throw new Error('Risposta dal server non valida')
      }

      // Normalize: backend returns flat roleName/roleId/level/permissions
      // but User type expects nested role: { id, name, level, permissions }
      const user: User = {
        ...rawUser,
        preferences: rawUser.preferences ?? {},
        role: rawUser.role ?? {
          id: rawUser.roleId ?? '',
          name: rawUser.roleName ?? 'VIEWER',
          level: rawUser.level ?? 5,
          permissions: rawUser.permissions ?? {},
          canDeletePed: rawUser.canDeletePed ?? false,
          canOrderSmm: rawUser.canOrderSmm ?? false,
        },
      }

      login(user, tokens)
      navigate(from, { replace: true })
    } catch (error) {
      setApiError(getApiErrorMessage(error))
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      {/* Email field */}
      <Input
        label="Email"
        type="email"
        placeholder="nome@azienda.it"
        autoComplete="email"
        icon={<Mail className="w-4 h-4" />}
        iconPosition="left"
        error={errors.email?.message ?? undefined}
        required
        {...register('email')}
      />

      {/* Password field with visibility toggle */}
      <div className="space-y-1.5">
        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••"
            autoComplete="current-password"
            icon={<Lock className="w-4 h-4" />}
            iconPosition="left"
            error={errors.password?.message ?? undefined}
            required
            {...register('password')}
          />
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute right-3 top-[2.125rem] text-slate-400 hover:text-slate-600 transition-colors"
            aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
          >
            {showPassword ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* API error message */}
      {apiError && (
        <div
          className="px-3 py-2.5 bg-danger-50 border border-danger-200 rounded-md text-sm text-danger-700"
          role="alert"
        >
          {apiError}
        </div>
      )}

      {/* Submit button */}
      <Button
        type="submit"
        variant="default"
        size="lg"
        loading={isSubmitting}
        className="w-full mt-2"
      >
        {isSubmitting ? 'Accesso in corso...' : 'Accedi'}
      </Button>
    </form>
  )
}
